import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Send as SendIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Description as ClaimIcon,
  CloudUpload as SubmitIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  Print as PrintIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { Claim, ClaimLine } from '../../types/Billing';

const ClaimGeneration: React.FC = () => {
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [submissionMethodFilter, setSubmissionMethodFilter] = useState<string>('all');
  const [openClaimDialog, setOpenClaimDialog] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Mock data - in real app, this would be an API call
      const mockClaims: Claim[] = [
        {
          id: 'claim-001',
          claimNumber: 'CLM-2024-001',
          patientId: 'PAT-001',
          providerId: 'PROV-001',
          facilityId: 'FAC-001',
          payerId: 'PAYER-001',
          claimType: 'professional',
          billingDate: '2024-01-15T10:00:00Z',
          serviceFromDate: '2024-01-15T08:00:00Z',
          serviceToDate: '2024-01-15T08:00:00Z',
          totalCharges: 110.00,
          totalAllowed: 78.82,
          totalPaid: 63.06,
          totalAdjustments: 15.76,
          patientResponsibility: 31.18,
          status: 'paid',
          submissionMethod: 'edi',
          batchId: 'BATCH-2024-001',
          acknowledgmentStatus: 'accepted',
          claimLines: [
            {
              id: 'line-001',
              lineNumber: 1,
              chargeEventId: 'ce-001',
              cptCode: '85025',
              modifiers: [],
              diagnosisCodes: ['Z00.00'],
              serviceDateFrom: '2024-01-15T08:00:00Z',
              serviceDateTo: '2024-01-15T08:00:00Z',
              placeOfService: '11',
              quantity: 1,
              billedAmount: 45.00,
              allowedAmount: 32.19,
              paidAmount: 25.75,
              adjustmentAmount: 6.44,
              deductibleAmount: 0,
              coinsuranceAmount: 6.44,
              copayAmount: 0,
              patientAmount: 12.81,
              status: 'paid',
              adjustmentReasons: ['CO-42: Charges exceed your contracted/legislated fee arrangement.']
            },
            {
              id: 'line-002',
              lineNumber: 2,
              chargeEventId: 'ce-002',
              cptCode: '80053',
              modifiers: [],
              diagnosisCodes: ['Z00.00'],
              serviceDateFrom: '2024-01-15T08:00:00Z',
              serviceDateTo: '2024-01-15T08:00:00Z',
              placeOfService: '11',
              quantity: 1,
              billedAmount: 65.00,
              allowedAmount: 46.63,
              paidAmount: 37.31,
              adjustmentAmount: 9.32,
              deductibleAmount: 0,
              coinsuranceAmount: 9.32,
              copayAmount: 0,
              patientAmount: 18.37,
              status: 'paid',
              adjustmentReasons: ['CO-42: Charges exceed your contracted/legislated fee arrangement.']
            }
          ],
          submittedAt: '2024-01-15T10:00:00Z',
          paidAt: '2024-01-20T14:30:00Z',
          createdAt: '2024-01-15T09:45:00Z',
          updatedAt: '2024-01-20T14:30:00Z'
        },
        {
          id: 'claim-002',
          claimNumber: 'CLM-2024-002',
          patientId: 'PAT-002',
          providerId: 'PROV-002',
          facilityId: 'FAC-001',
          payerId: 'PAYER-002',
          claimType: 'professional',
          billingDate: '2024-01-16T10:00:00Z',
          serviceFromDate: '2024-01-16T09:00:00Z',
          serviceToDate: '2024-01-16T09:00:00Z',
          totalCharges: 125.00,
          totalAllowed: 0,
          totalPaid: 0,
          totalAdjustments: 0,
          patientResponsibility: 0,
          status: 'submitted',
          submissionMethod: 'edi',
          batchId: 'BATCH-2024-002',
          acknowledgmentStatus: 'pending',
          claimLines: [
            {
              id: 'line-003',
              lineNumber: 1,
              chargeEventId: 'ce-003',
              cptCode: '85025',
              modifiers: [],
              diagnosisCodes: ['Z00.00'],
              serviceDateFrom: '2024-01-16T09:00:00Z',
              serviceDateTo: '2024-01-16T09:00:00Z',
              placeOfService: '11',
              quantity: 1,
              billedAmount: 45.00,
              allowedAmount: 0,
              paidAmount: 0,
              adjustmentAmount: 0,
              deductibleAmount: 0,
              coinsuranceAmount: 0,
              copayAmount: 0,
              patientAmount: 0,
              status: 'pending',
              adjustmentReasons: []
            }
          ],
          submittedAt: '2024-01-16T10:00:00Z',
          createdAt: '2024-01-16T09:30:00Z',
          updatedAt: '2024-01-16T10:00:00Z'
        }
      ];

      setClaims(mockClaims);
    } catch (error) {
      console.error('Error loading claims data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDateTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'submitted':
        return 'info';
      case 'accepted':
        return 'primary';
      case 'rejected':
        return 'error';
      case 'paid':
        return 'success';
      case 'denied':
        return 'error';
      case 'appealed':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getAcknowledgmentIcon = (status?: string) => {
    switch (status) {
      case 'accepted':
        return <CheckIcon color="success" />;
      case 'rejected':
        return <ErrorIcon color="error" />;
      case 'pending':
        return <ScheduleIcon color="warning" />;
      default:
        return <WarningIcon color="disabled" />;
    }
  };

  const filteredClaims = claims.filter(claim => {
    const matchesSearch = claim.claimNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         claim.patientId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || claim.status === statusFilter;
    const matchesMethod = submissionMethodFilter === 'all' || claim.submissionMethod === submissionMethodFilter;
    return matchesSearch && matchesStatus && matchesMethod;
  });

  const handleViewClaim = (claim: Claim) => {
    setSelectedClaim(claim);
    setOpenClaimDialog(true);
  };

  const handleBatchSubmission = async () => {
    setBatchProcessing(true);
    setBatchProgress(0);

    // Simulate batch processing
    const draftClaims = claims.filter(claim => claim.status === 'draft');
    const totalClaims = draftClaims.length;

    for (let i = 0; i < totalClaims; i++) {
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate processing time
      setBatchProgress(((i + 1) / totalClaims) * 100);
    }

    setBatchProcessing(false);
    setBatchProgress(0);
    // In real app, would update claims status to 'submitted'
    Alert({
      severity: 'success', 
      children: 'Batch submission completed successfully'
    });
  };

  const getDraftClaimsCount = () => {
    return claims.filter(claim => claim.status === 'draft').length;
  };

  const getSubmittedClaimsCount = () => {
    return claims.filter(claim => claim.status === 'submitted').length;
  };

  const getPaidClaimsCount = () => {
    return claims.filter(claim => claim.status === 'paid').length;
  };

  const getTotalCharges = () => {
    return claims.reduce((sum, claim) => sum + claim.totalCharges, 0);
  };

  const getTotalPaid = () => {
    return claims.reduce((sum, claim) => sum + claim.totalPaid, 0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Claim Generation & Submission
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
          >
            Print Claims
          </Button>
          <Button
            variant="contained"
            startIcon={<SubmitIcon />}
            onClick={handleBatchSubmission}
            disabled={batchProcessing || getDraftClaimsCount() === 0}
          >
            Batch Submit ({getDraftClaimsCount()})
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ClaimIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Total Claims
                </Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {claims.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatCurrency(getTotalCharges())} total charges
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScheduleIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Pending
                </Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {getSubmittedClaimsCount()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Awaiting processing
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Paid
                </Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {getPaidClaimsCount()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatCurrency(getTotalPaid())} collected
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SendIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Ready to Submit
                </Typography>
              </Box>
              <Typography variant="h4" color="info.main">
                {getDraftClaimsCount()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Draft claims ready
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Batch Processing Progress */}
      {batchProcessing && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Batch Submission in Progress
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={batchProgress} 
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {Math.round(batchProgress)}% complete
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Claims Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              label="Search claims"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
              sx={{ minWidth: 250 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="submitted">Submitted</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="denied">Denied</MenuItem>
                <MenuItem value="appealed">Appealed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Submission Method</InputLabel>
              <Select
                value={submissionMethodFilter}
                label="Submission Method"
                onChange={(e) => setSubmissionMethodFilter(e.target.value)}
              >
                <MenuItem value="all">All Methods</MenuItem>
                <MenuItem value="edi">EDI</MenuItem>
                <MenuItem value="paper">Paper</MenuItem>
                <MenuItem value="clearinghouse">Clearinghouse</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Claim Number</TableCell>
                  <TableCell>Patient ID</TableCell>
                  <TableCell>Service Date</TableCell>
                  <TableCell align="right">Total Charges</TableCell>
                  <TableCell align="right">Total Paid</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submission</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClaims.map((claim) => (
                  <TableRow key={claim.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {claim.claimNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {claim.claimType}
                      </Typography>
                    </TableCell>
                    <TableCell>{claim.patientId}</TableCell>
                    <TableCell>
                      {formatDateTime(claim.serviceFromDate)}
                      {claim.serviceFromDate !== claim.serviceToDate && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          to {formatDateTime(claim.serviceToDate)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(claim.totalCharges)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(claim.totalPaid)}
                      </Typography>
                      {claim.patientResponsibility > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Patient: {formatCurrency(claim.patientResponsibility)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={claim.status.charAt(0).toUpperCase() + claim.status.slice(1)}
                        color={getStatusColor(claim.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {claim.submissionMethod.toUpperCase()}
                        </Typography>
                        {getAcknowledgmentIcon(claim.acknowledgmentStatus)}
                      </Box>
                      {claim.batchId && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Batch: {claim.batchId}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Claim Details">
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewClaim(claim)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Claim Details Dialog */}
      <Dialog 
        open={openClaimDialog} 
        onClose={() => setOpenClaimDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Claim Details: {selectedClaim?.claimNumber}
        </DialogTitle>
        <DialogContent>
          {selectedClaim && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Claim Information
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Patient ID" 
                        secondary={selectedClaim.patientId} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Provider ID" 
                        secondary={selectedClaim.providerId} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Service Date Range" 
                        secondary={`${formatDateTime(selectedClaim.serviceFromDate)} - ${formatDateTime(selectedClaim.serviceToDate)}`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Submission Method" 
                        secondary={selectedClaim.submissionMethod.toUpperCase()} 
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Financial Summary
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Total Charges" 
                        secondary={formatCurrency(selectedClaim.totalCharges)} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Total Allowed" 
                        secondary={formatCurrency(selectedClaim.totalAllowed)} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Total Paid" 
                        secondary={formatCurrency(selectedClaim.totalPaid)} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Patient Responsibility" 
                        secondary={formatCurrency(selectedClaim.patientResponsibility)} 
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Claim Lines
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Line</TableCell>
                      <TableCell>CPT Code</TableCell>
                      <TableCell>Diagnosis</TableCell>
                      <TableCell align="right">Billed</TableCell>
                      <TableCell align="right">Allowed</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedClaim.claimLines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell>{line.lineNumber}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {line.cptCode}
                          </Typography>
                          {line.modifiers.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Mods: {line.modifiers.join(', ')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {line.diagnosisCodes.join(', ')}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(line.billedAmount)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(line.allowedAmount)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(line.paidAmount)}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={line.status}
                            color={getStatusColor(line.status) as any}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenClaimDialog(false)}>Close</Button>
          <Button variant="contained" startIcon={<PrintIcon />}>
            Print Claim
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClaimGeneration;